import { z } from "zod";
import { countryCode, isoDateTime, optionalEmail, optionalText, phoneInput, text, timeZone, uuid, wallTime } from "./common";

export const branchInput = z.object({
  code: z.string().trim().toUpperCase().regex(/^[A-Z0-9][A-Z0-9-]{0,19}$/, "Letters, digits and dashes (max 20)"),
  name: text(200),
  phone: phoneInput,
  email: optionalEmail,
  addressLine1: optionalText(200),
  addressLine2: optionalText(200),
  city: optionalText(100),
  region: optionalText(100),
  postalCode: optionalText(20),
  countryCode,
  timeZone,
  isActive: z.boolean().default(true),
  /** Seller tax id on this branch's invoices (India: GSTIN). Empty = the clinic-level number. */
  taxRegistrationNumber: z.string().trim().toUpperCase().max(30).nullish().transform((v) => v || null),
  /** Tax region (India: two-digit GST state code, e.g. 32 for Kerala). Needed for GST invoices. */
  taxRegionCode: z.string().trim().toUpperCase().regex(/^[A-Z0-9]{1,10}$/, "e.g. 32").nullish().or(z.literal("")).transform((v) => v || null),
});
export type BranchInput = z.infer<typeof branchInput>;
export const updateBranchInput = branchInput.extend({ version: z.number().int().min(1) });

export interface Branch extends Omit<BranchInput, "phone" | "email"> {
  id: string;
  phone: string | null;
  email: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
}

/** ISO weekday 1 = Monday ... 7 = Sunday. */
export const weekday = z.number().int().min(1).max(7);

export const hoursBlock = z
  .object({ weekday, opensAt: wallTime, closesAt: wallTime })
  .refine((b) => b.closesAt > b.opensAt, { message: "Closing time must be after opening time", path: ["closesAt"] });
export const setBranchHoursInput = z.object({ hours: z.array(hoursBlock).max(70) });
export type HoursBlock = z.infer<typeof hoursBlock>;

export const closureInput = z
  .object({
    branchId: uuid.nullable(),
    kind: z.enum(["holiday", "emergency", "other"]),
    name: text(200),
    startsAt: isoDateTime,
    endsAt: isoDateTime,
  })
  .refine((c) => new Date(c.endsAt) > new Date(c.startsAt), { message: "End must be after start", path: ["endsAt"] });
export type ClosureInput = z.infer<typeof closureInput>;
export interface Closure extends ClosureInput {
  id: string;
}

/** gender: who the room is for (Ayurveda therapy rooms are often separate for men and women). */
export const roomInput = z.object({ name: text(100), notes: optionalText(500), isActive: z.boolean().default(true), gender: z.enum(["any", "male", "female"]).default("any") });
export const chairInput = z.object({
  name: text(100),
  roomId: uuid.nullable().default(null),
  notes: optionalText(500),
  isActive: z.boolean().default(true),
});
export interface Room {
  id: string;
  branchId: string;
  name: string;
  notes: string | null;
  isActive: boolean;
  gender?: "any" | "male" | "female";
}
export interface Chair extends Room {
  roomId: string | null;
}
